import DetailsCard from "@/components/DetailsCard";
import FocusedPageLayout from "@/layouts/FocusedPageLayout";
import { Path, useNavigate, useParams } from "@/router";
import { PersonObjectHeading } from "@/features/person/components/PersonObjectHeading";
import { useQuery } from "@apollo/client";
import { GET_COMPENSATION_PROFILES_QUERY } from "../../queries";
import { formatDisplayCurrency } from "@/lib/currencyHelpers";
import Button from "@/components/Button";
import MetricGrid from "@/components/MetricGrid";
import { CardSkeleton } from "@/components/Skeleton";
import { useLocation } from "react-router-dom";
import { PERSON_DETAILS_HEADER_QUERY } from "@/features/person/queries";
import { renderName } from "@/lib/personHelpers";
import { getCompensationRateFrequency } from "@/lib/compensationRateHelpers";
import usePageTitle from "@/hooks/usePageTitle";
import { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";
import { getInitialTotalCompensationProfile } from "@/components/Forms/CompensationProfileForm/initialTotalCompensationProfile";
import { buildCompensationProfileMetrics } from "./compensationProfileMetrics";

export default function CompensationProfileView() {
  const { pgaId, compProfileId } = useParams("/people/:pgaId/career/compensation/:compProfileId/view");
  const navigate = useNavigate();
  const location = useLocation();

  const { data: personData } = useQuery(PERSON_DETAILS_HEADER_QUERY, {
    variables: { pgaId: pgaId || "" },
    skip: !pgaId
  });

  const personName = personData?.person
    ? renderName(
        personData.person.displayName,
        personData.person.informalName,
        personData.person.preferredLastName,
        personData.person.suffixName,
        personData.person.middleName,
        personData.person.firstName
      )
    : "";
  usePageTitle(location.pathname, personName ? `People | ${personName}` : "People");
  const backLink = {
    label: "Back",
    to: "/people/:pgaId/career/compensation" as Path,
    params: { pgaId: pgaId }
  } as any;

  const { data, loading, error } = useQuery(GET_COMPENSATION_PROFILES_QUERY, {
    variables: { pgaId: pgaId }
  });

  const compensationProfiles = data?.person?.compensationProfiles ?? [];
  const initialTotalCompensationProfile = getInitialTotalCompensationProfile(compensationProfiles);
  const profile = compensationProfiles.find((profile: any) => profile.id === compProfileId);
  const canShowInitialTotalCompensation = profile?.id === initialTotalCompensationProfile?.id;
  const formatViewCurrency = (value: number | null | undefined) => formatDisplayCurrency(value, 0);
  const canManageCompensationProfiles = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.manageCompensationProfiles
  });

  const actions =
    profile && canManageCompensationProfiles ? (
      <Button
        variant="outlined"
        onClick={() => navigate(`/people/${pgaId}/career/compensation/${profile.id}/edit` as Path)}
        data-testid="edit-profile"
      >
        Edit Profile
      </Button>
    ) : null;

  const extendedActions = [] as any;

  // Build options arrays for each DetailsCard
  const compensationSummaryOptions = [
    profile?.basePay && {
      iconName: "attach_money",
      overline: "Base Pay (Annual)",
      headline: `$${formatViewCurrency(profile.basePay)}`
    },
    profile?.bonusIncome && {
      iconName: "attach_money",
      overline: "Bonus Income",
      headline: `$${formatViewCurrency(profile.bonusIncome)} / year`
    },
    profile?.lessonIncome && {
      iconName: "attach_money",
      overline: "Lesson Income",
      headline: `$${formatViewCurrency(profile.lessonIncome)} / year`
    },
    profile?.otherIncome && {
      iconName: "attach_money",
      overline: "Other Income",
      headline: `$${formatViewCurrency(profile.otherIncome)} / year`
    },
    canShowInitialTotalCompensation &&
      profile?.initialTotalCompensation != null && {
        iconName: "attach_money",
        overline: "Initial Total Compensation",
        headline: `$${formatViewCurrency(profile.initialTotalCompensation)} / year`
      },
    profile?.totalCompensation && {
      iconName: "attach_money",
      overline: "Total Compensation",
      headline: `$${formatViewCurrency(profile.totalCompensation)} / year`
    },
    profile?.basePayType && {
      iconName: "paid",
      overline: "Base Pay Type",
      headline: profile.basePayType
    },
    profile?.basePayType &&
      profile?.payRate && {
        iconName: "attach_money",
        overline: "Pay Rate",
        headline: `$${formatViewCurrency(profile.payRate)} / ${getCompensationRateFrequency(profile.basePayType)}`
      },
    profile?.totalMonthsWorkedAnnual && {
      iconName: "calendar_today",
      overline: "Number of Months Worked Annually",
      headline: profile.totalMonthsWorkedAnnual
    }
  ].filter(Boolean);

  const golfShopOptions = [
    profile?.ownsGolfShop && {
      iconName: "sports_golf",
      overline: "Golf Shop Ownership/Commission",
      headline: "Self-Owned"
    },
    profile?.golfShopOwnershipPercent && {
      iconName: "percent",
      overline: "Percent of Ownership",
      headline: `${profile.golfShopOwnershipPercent}`
    },
    profile?.golfShopGrossSales && {
      iconName: "attach_money",
      overline: "Golf Shop Gross Sales",
      headline: `$${formatViewCurrency(profile.golfShopGrossSales)} / year`
    },
    profile?.golfShopCommissionAnnualIncome && {
      iconName: "attach_money",
      overline: "Total Merchandise Concession Income",
      headline: `$${formatViewCurrency(profile.golfShopCommissionAnnualIncome)} / year`
    },
    profile?.golfShopNetIncome && {
      iconName: "attach_money",
      overline: "Golf Shop Net Income",
      headline: `$${formatViewCurrency(profile.golfShopNetIncome)} / year`
    },
    profile?.golfShopComissionAnnualIncome && {
      iconName: "attach_money",
      overline: "Golf Shop Commission Annual Income",
      headline: `${profile.golfShopComissionAnnualIncome}`
    },
    profile?.golfShopCommissionPercentage && {
      iconName: "percent",
      overline: "Golf Shop Commission Percentage",
      headline: `${profile.golfShopCommissionPercentage}`
    }
  ].filter(Boolean);

  const lessonIncomeOptions = [
    profile?.pgaJrlIncome && {
      iconName: "attach_money",
      overline: "PGA Junior League Income",
      headline: `$${formatViewCurrency(profile.pgaJrlIncome)} / year`
    },
    profile?.privateLessonIncome && {
      iconName: "attach_money",
      overline: "Private Lesson Income",
      headline: `$${formatViewCurrency(profile.privateLessonIncome)} / year`
    },
    profile?.groupLessonIncome && {
      iconName: "attach_money",
      overline: "Group Lesson Income",
      headline: `$${formatViewCurrency(profile.groupLessonIncome)} / year`
    },
    profile?.otherLessonIncome && {
      iconName: "attach_money",
      overline: "Other Lesson Income",
      headline: `$${formatViewCurrency(profile.otherLessonIncome)} / year`
    },
    (profile?.privateLessonIncome ||
      profile?.groupLessonIncome ||
      profile?.otherLessonIncome ||
      profile?.pgaJrlIncome) && {
      iconName: "attach_money",
      overline: "Total Lesson Income",
      headline: `$${formatViewCurrency(
        (profile.privateLessonIncome || 0) +
          (profile.groupLessonIncome || 0) +
          (profile.otherLessonIncome || 0) +
          (profile.pgaJrlIncome || 0)
      )} / year`
    },
    profile?.lessonPercentageSplit && {
      iconName: "percent",
      overline: "Percent Lesson Income Retained by Facility",
      headline: `${profile.lessonPercentageSplit}`
    }
  ].filter(Boolean);

  const benefitOptions = [
    profile?.employerOffers401k && {
      iconName: "check_circle",
      headline: "My employer offers a 401k"
    },
    profile?.employerMatches401k && {
      iconName: "check_circle",
      headline: "My employer matches my 401k"
    },
    profile?.employerMatch401kPercent && {
      iconName: "percent",
      overline: "Employer Match Percentage",
      headline: `${profile.employerMatch401kPercent}`
    },
    profile?.employerOffersHealthcare && {
      iconName: "check_circle",
      headline: "My employer offers healthcare coverage"
    },
    profile?.employerOffersDental && {
      iconName: "check_circle",
      headline: "My employer offers dental coverage"
    },
    profile?.employerOffersVision && {
      iconName: "check_circle",
      headline: "My employer offers vision coverage"
    },
    profile?.employerOffersLifeInsurance && {
      iconName: "check_circle",
      headline: "My employer offers life insurance"
    },
    profile?.employerOffersDisability && {
      iconName: "check_circle",
      headline: "My employer offers disability coverage"
    },
    profile?.employerOffersPaidVacation && {
      iconName: "check_circle",
      headline: "My employer offers PTO"
    },
    profile?.employerOffersPaidVacationWeekAmount && {
      iconName: "calendar_today",
      overline: "Annual Weeks of PTO",
      headline: profile.employerOffersPaidVacationWeekAmount
    },
    profile?.employerOffersEducationAllowance && {
      iconName: "check_circle",
      headline: "My employer offers an education allowance"
    },
    profile?.employerOffersEducationAllowanceAmount && {
      iconName: "attach_money",
      overline: "Amount of Education Allowance",
      headline: `$${formatViewCurrency(profile.employerOffersEducationAllowanceAmount)}`
    },
    profile?.employerOffersTournamentAllowance && {
      iconName: "check_circle",
      headline: "My employer offers a tournament allowance"
    },
    profile?.employerOffersTournamentAllowanceAmount && {
      iconName: "attach_money",
      overline: "Amount of Tournament Allowance",
      headline: `$${formatViewCurrency(profile.employerOffersTournamentAllowanceAmount)}`
    },
    profile?.employerOffersCellphoneAllowance && {
      iconName: "check_circle",
      headline: "My employer offers a cellphone allowance"
    },
    profile?.employerOffersCellphoneAllowanceAmount && {
      iconName: "attach_money",
      overline: "Amount of Cellphone Allowance",
      headline: `$${formatViewCurrency(profile.employerOffersCellphoneAllowanceAmount)}`
    },
    profile?.employerOffersCarAllowance && {
      iconName: "check_circle",
      headline: "My employer offers a car allowance"
    },
    profile?.employerOffersCarAllowanceAmount && {
      iconName: "attach_money",
      overline: "Amount of Car Allowance",
      headline: `$${formatViewCurrency(profile.employerOffersCarAllowanceAmount)}`
    },
    profile?.employerOffersClothingAllowance && {
      iconName: "check_circle",
      headline: "My employer offers a clothing allowance"
    },
    profile?.employerOffersClothingAllowanceAmount && {
      iconName: "attach_money",
      overline: "Amount of Clothing Allowance",
      headline: `$${formatViewCurrency(profile.employerOffersClothingAllowanceAmount)}`
    },
    profile?.participateInGrp && {
      iconName: "check_circle",
      headline: "I participate in Golf Retirement Plus"
    },
    profile?.otherEmploymentBenefitsDescription && {
      iconName: "description",
      overline: "Employment Benefits Description",
      headline: profile.otherEmploymentBenefitsDescription
    }
  ].filter(Boolean);

  const otherIncomeOptions = [
    profile?.eventOutingCommissionAmount && {
      iconName: "check_circle",
      headline: "I receive income from events/outings"
    },
    profile?.eventOutingCommissionAmount && {
      iconName: "attach_money",
      overline: "Event/Outing Income",
      headline: `$${formatViewCurrency(profile.eventOutingCommissionAmount)}`
    },
    profile?.eventOutingCommissionPercent && {
      iconName: "percent",
      overline: "Event/Outing Commission Percentage",
      headline: `${profile.eventOutingCommissionPercent}`
    },
    profile?.facilityCommissionAmount && {
      iconName: "check_circle",
      headline: "I receive income from facility revenue"
    },
    profile?.facilityCommissionAmount && {
      iconName: "attach_money",
      overline: "Facility Income",
      headline: `$${formatViewCurrency(profile.facilityCommissionAmount)}`
    },
    profile?.facilityCommissionPercent && {
      iconName: "percent",
      overline: "Facility Commission Percentage",
      headline: `${profile.facilityCommissionPercent}`
    },
    profile?.foodBeverageCommissionAmount && {
      iconName: "check_circle",
      headline: "I receive income from food and beverage revenue"
    },
    profile?.foodBeverageCommissionAmount && {
      iconName: "attach_money",
      overline: "Food and Beverage Income",
      headline: `$${formatViewCurrency(profile.foodBeverageCommissionAmount)}`
    },
    profile?.foodBeverageCommissionPercent && {
      iconName: "percent",
      overline: "Food and Beverage Commission Percentage",
      headline: `${profile.foodBeverageCommissionPercent}`
    },
    profile?.golfCarCommissionAmount && {
      iconName: "check_circle",
      headline: "I receive income from golf car revenue"
    },
    profile?.golfCarCommissionAmount && {
      iconName: "attach_money",
      overline: "Golf Car Income",
      headline: `$${formatViewCurrency(profile.golfCarCommissionAmount)}`
    },
    profile?.golfCarCommissionPercent && {
      iconName: "percent",
      overline: "Golf Car Commission Percentage",
      headline: `${profile.golfCarCommissionPercent}`
    },
    profile?.greenFeeCommissionAmount && {
      iconName: "check_circle",
      headline: "I receive income from green fee revenue"
    },
    profile?.greenFeeCommissionAmount && {
      iconName: "attach_money",
      overline: "Green Fee Income",
      headline: `$${formatViewCurrency(profile.greenFeeCommissionAmount)}`
    },
    profile?.greenFeeCommissionPercent && {
      iconName: "percent",
      overline: "Green Fee Commission Percentage",
      headline: `${profile.greenFeeCommissionPercent}`
    },
    profile?.membershipSalesCommissionAmount && {
      iconName: "check_circle",
      headline: "I receive income from membership sales revenue"
    },
    profile?.membershipSalesCommissionAmount && {
      iconName: "attach_money",
      overline: "Membership Sales Income",
      headline: `$${formatViewCurrency(profile.membershipSalesCommissionAmount)}`
    },
    profile?.membershipSalesCommissionPercent && {
      iconName: "percent",
      overline: "Membership Sales Commission Percentage",
      headline: `${profile.membershipSalesCommissionPercent}`
    },
    profile?.practiceRangeCommissionAmount && {
      iconName: "check_circle",
      headline: "I receive income from practice range revenue"
    },
    profile?.practiceRangeCommissionAmount && {
      iconName: "attach_money",
      overline: "Practice Range Income",
      headline: `$${formatViewCurrency(profile.practiceRangeCommissionAmount)}`
    },
    profile?.practiceRangeCommissionPercent && {
      iconName: "percent",
      overline: "Practice Range Commission Percentage",
      headline: `${profile.practiceRangeCommissionPercent}`
    },
    profile?.clubFittingCommissionAmount && {
      iconName: "check_circle",
      headline: "I receive income from club fitting revenue"
    },
    profile?.clubFittingCommissionAmount && {
      iconName: "attach_money",
      overline: "Club Fitting Income",
      headline: `$${formatViewCurrency(profile.clubFittingCommissionAmount)}`
    },
    profile?.clubFittingCommissionPercent && {
      iconName: "percent",
      overline: "Club Fitting Commission Percentage",
      headline: `${profile.clubFittingCommissionPercent}`
    },
    profile?.clubRentalCommissionAmount && {
      iconName: "check_circle",
      headline: "I receive income from club rental revenue"
    },
    profile?.clubRentalCommissionAmount && {
      iconName: "attach_money",
      overline: "Club Rental Income",
      headline: `$${formatViewCurrency(profile.clubRentalCommissionAmount)}`
    },
    profile?.clubRentalCommissionPercent && {
      iconName: "percent",
      overline: "Club Rental Commission Percentage",
      headline: `${profile.clubRentalCommissionPercent}`
    },
    profile?.clubRepairCommissionAmount && {
      iconName: "check_circle",
      headline: "I receive income from club repair revenue"
    },
    profile?.clubRepairCommissionAmount && {
      iconName: "attach_money",
      overline: "Club Repair Income",
      headline: `$${formatViewCurrency(profile.clubRepairCommissionAmount)}`
    },
    profile?.clubRepairCommissionPercent && {
      iconName: "percent",
      overline: "Club Repair Commission Percentage",
      headline: `${profile.clubRepairCommissionPercent}`
    },
    profile?.clubStorageCommissionAmount && {
      iconName: "check_circle",
      headline: "I receive income from club storage revenue"
    },
    profile?.clubStorageCommissionAmount && {
      iconName: "attach_money",
      overline: "Club Storage Income",
      headline: `$${formatViewCurrency(profile.clubStorageCommissionAmount)}`
    },
    profile?.clubStorageCommissionPercent && {
      iconName: "percent",
      overline: "Club Storage Commission Percentage",
      headline: `${profile.clubStorageCommissionPercent}`
    }
  ].filter(Boolean);

  const profileMetrics = buildCompensationProfileMetrics(profile, loading);

  return (
    <FocusedPageLayout backLink={backLink} actions={actions} extendedActions={extendedActions} stickyActions={true}>
      <PersonObjectHeading pgaId={pgaId} hideActions disableScrollAdjustment />
      <div data-testid="compensation-profile-metrics">
        <MetricGrid metrics={profileMetrics} layout="expanded" />
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 pt-8">
          <div className="flex flex-col gap-4 md:col-span-1">
            <CardSkeleton className="!h-fit !w-full" />
            <CardSkeleton className="!h-fit !w-full" />
          </div>
          <div className="flex flex-col gap-4 md:col-span-2">
            <CardSkeleton className="!h-fit !w-full" />
            <CardSkeleton className="!h-fit !w-full" />
          </div>
        </div>
      ) : error ? (
        <div className="p-8 text-sys-error">Error loading compensation profile.</div>
      ) : profile ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 pt-8">
          <div className="flex flex-col gap-4 md:col-span-1">
            {compensationSummaryOptions.length > 0 && (
              <DetailsCard className="!h-fit" heading="Compensation Summary" options={compensationSummaryOptions} />
            )}
            {golfShopOptions.length > 0 && (
              <DetailsCard className="!h-fit" heading="Golf Shop Details" options={golfShopOptions} />
            )}
            {lessonIncomeOptions.length > 0 && (
              <DetailsCard className="!h-fit" heading="Lesson Income" options={lessonIncomeOptions} />
            )}
          </div>
          <div className="flex flex-col gap-4 md:col-span-2">
            {benefitOptions.length > 0 && (
              <DetailsCard className="!h-fit" heading="Benefit Details" options={benefitOptions} />
            )}
            {otherIncomeOptions.length > 0 && (
              <DetailsCard className="!h-fit" heading="Other Income Details" options={otherIncomeOptions} />
            )}
          </div>
        </div>
      ) : null}
    </FocusedPageLayout>
  );
}
